import {AlertIcon} from '@primer/octicons-react'
import {Pagination} from '@primer/react'
import type {UITask} from '../../types/task'
import {SimpleListView} from '@github-ui/simple-list-view'
import {SimpleListHeader} from '@github-ui/simple-list-view/SimpleListHeader'
import {SimpleListItem} from '@github-ui/simple-list-view/SimpleListItem'
import {ListItemsSkeletonLoader} from './ListParts'
import {Blankslate, SkeletonText} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {MouseEvent, ReactNode} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {parsePageNumber} from '../../utils/url-helpers'

import styles from './TaskListLayout.module.css'

export interface TaskListLayoutProps {
  tasks: UITask[]
  isError: boolean
  isLoading: boolean
  showSkeletonLoaders: boolean
  hasNextPage: boolean
  header: ReactNode
  emptyHeading: string
  emptyDescription: ReactNode
  errorText: string
  renderItem: (task: UITask) => ReactNode
}

/**
 * Shared structural component for task list views.
 * Handles layout, error/empty/skeleton states, and pagination.
 * Each list variant supplies its own header content and item renderer.
 */
export function TaskListLayout({
  tasks,
  isError,
  isLoading,
  showSkeletonLoaders,
  hasNextPage,
  header,
  emptyHeading,
  emptyDescription,
  errorText,
  renderItem,
}: TaskListLayoutProps) {
  return (
    <div className={styles.list}>
      <SimpleListView>
        <SimpleListHeader className={styles.header}>
          <SimpleListHeader.Metadata>{header}</SimpleListHeader.Metadata>
        </SimpleListHeader>
        {tasks.length === 0 && !isError && !isLoading ? (
          <Blankslate className={styles.listContainer}>
            {showSkeletonLoaders ? (
              <>
                <SkeletonText size="titleMedium" />
                <SkeletonText size="bodyMedium" lines={2} />
              </>
            ) : (
              <>
                <Blankslate.Heading>{emptyHeading}</Blankslate.Heading>
                <Blankslate.Description>{emptyDescription}</Blankslate.Description>
              </>
            )}
          </Blankslate>
        ) : (
          <SimpleListView.Items className={clsx(styles.listContainer, styles.items)}>
            {isError ? (
              <SimpleListItem className={styles.emptyItem}>
                <AlertIcon className={styles.noItemsIcon} />
                <p className={styles.emptyTitle}>{errorText}</p>
              </SimpleListItem>
            ) : showSkeletonLoaders ? (
              <ListItemsSkeletonLoader />
            ) : (
              <>{tasks.map(task => renderItem(task))}</>
            )}
          </SimpleListView.Items>
        )}
      </SimpleListView>
      <TaskListPagination hasNextPage={hasNextPage} hidden={showSkeletonLoaders} />
    </div>
  )
}

function TaskListPagination({hasNextPage, hidden}: {hasNextPage: boolean; hidden: boolean}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = parsePageNumber(searchParams.get('page'))
  const hasPreviousPage = currentPage > 1
  // Workaround until we return total task count from the tasks API
  const pageCount = hasNextPage ? currentPage + 1 : currentPage

  const onPageChange = (e: MouseEvent, page: number): void => {
    e.preventDefault()
    searchParams.set('page', page.toString())
    setSearchParams(searchParams)
  }

  if (hidden || (!hasNextPage && !hasPreviousPage)) return <></>
  return <Pagination showPages={false} pageCount={pageCount} currentPage={currentPage} onPageChange={onPageChange} />
}
